// --- UPDATE (v2 — FlowSense AI dashboard)
//   • FlowSense AI Command Center widget (Ollama + ML + rules) and Ecosystem widget
//   • "FlowSense AI & Guide" tab: full AI command center + every smart module explained
//   • FlowSense guide drawer + "?" help on every widget (what it shows, how to act)
//   • One shared live snapshot (FlowSenseIntelProvider) and live activity badge
//   • Widget registry descriptions (used by the Widget Library search + guide)
// --- END UPDATE
import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  Button,
  useToast,
  IconButton,
  useDisclosure,
  Spinner,
  Center,
  HStack,
  Badge,
  Icon,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Switch,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Heading,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import {
  AddIcon,
  CloseIcon,
  SettingsIcon,
  LockIcon,
  UnlockIcon,
  RepeatIcon,
  InfoOutlineIcon, // --- UPDATE
} from '@chakra-ui/icons';
import { FaSave, FaUndo, FaExpand, FaCompress } from 'react-icons/fa';
import { MdWifi, MdWifiOff, MdAutoAwesome } from 'react-icons/md'; // --- UPDATE: Imported Wi-Fi Icons (+ AI icon)
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

// Framer Motion for animations
import { motion, AnimatePresence } from 'framer-motion';

// --- Ensure this file exists in your directory to resolve the Webpack error ---
import LiveGrid from './LiveGrid';

import { getUserInfo } from '../../utils/auth';
import { getDashboardLayout, saveDashboardLayout } from '../../services/settingsService';

// --- UPDATE: Import Global WebSocket Context ---
import { useNotifications } from '../../contexts/NotificationContext';
// --- UPDATE: FlowSense AI (shared snapshot, guide drawer, widget help)
import { FlowSenseIntelProvider, useFlowSenseIntel } from '../../components/dashboard/ai/FlowSenseIntelContext';
import DashboardAiHelper, { WidgetHelpButton } from '../../components/dashboard/ai/DashboardAiHelper';
// --- END UPDATE

// --- PERFORMANCE UPDATE: Lazy Load all Widgets ---
// This defers downloading the Javascript for these widgets until they are actually rendered on screen.
const AssetsAtRiskWidget        = lazy(() => import('../../components/dashboard/widgets/AssetsAtRiskWidget'));
const LowStockWidget            = lazy(() => import('../../components/dashboard/widgets/LowStockWidget'));
const LiveGisMapWidget          = lazy(() => import('../../components/dashboard/widgets/LiveGisMapWidget'));
const StatCardsGroupWidget      = lazy(() => import('../../components/dashboard/widgets/StatCardsGroupWidget'));
const InventoryValuationWidget  = lazy(() => import('../../components/dashboard/widgets/InventoryValuationWidget'));
const WorkOrdersChartWidget     = lazy(() => import('../../components/dashboard/widgets/WorkOrdersChartWidget'));
const RecentIssuesWidget        = lazy(() => import('../../components/dashboard/widgets/RecentIssuesWidget'));
const WorkOrderStatusDonutWidget = lazy(() => import('../../components/dashboard/widgets/WorkOrderStatusDonutWidget'));
const SystemHealthRadialWidget  = lazy(() => import('../../components/dashboard/widgets/SystemHealthRadialWidget'));
const WidgetLibraryDrawer       = lazy(() => import('../../components/dashboard/widgets/WidgetLibraryDrawer'));

// --- UPDATE: Lazy load the new SLA Tracker Widget ---
const SlaTrackerWidget = lazy(() => import('../../components/dashboard/widgets/SlaTrackerWidget'));
// --- END UPDATE ---
// --- UPDATE: FlowSense AI widgets
const FlowSenseAiCommandCenter = lazy(() => import('../../components/dashboard/widgets/FlowSenseAiCommandCenter'));
const FlowSenseModulesWidget   = lazy(() => import('../../components/dashboard/widgets/FlowSenseModulesWidget'));
const AI_BANNER_KEY = 'flowSense_aiBannerDismissed';
// --- END UPDATE

const ResponsiveGridLayout = WidthProvider(Responsive);

// ============================================================================
// MOTION COMPONENTS & VARIANTS
// ============================================================================
const MotionBox = motion(Box);

const pulseAnimation = {
    opacity: [1, 0.4, 1],
    scale: [1, 1.2, 1],
    transition: { duration: 2, repeat: Infinity, ease: "easeInOut" }
};

// ============================================================================
// WIDGET REGISTRY
// ============================================================================
const ALL_AVAILABLE_WIDGETS = {
  // --- UPDATE: FlowSense AI widgets + descriptions on every widget (Widget Library search / guide)
  ai:         { name: 'FlowSense AI Command Center', description: 'AI brief of the whole system, top priorities with one-click actions, live activity and Ask-AI (Ollama, ML, rules).', component: <FlowSenseAiCommandCenter />, defaultLayout: { w: 8, h: 8, x: 0, y: Infinity }, category: 'AI/ML' },
  modules:    { name: 'FlowSense Ecosystem', description: 'Every smart module — Work Orders, Road Works, Live Readings, Public Utilities, Hub … — with live status and plain-language explanations.', component: <FlowSenseModulesWidget />, defaultLayout: { w: 12, h: 6, x: 0, y: Infinity }, category: 'Overview' },
  // --- END UPDATE
  stats:      { name: 'Stat Cards', description: 'Open work orders, unresolved issues and active sensor alerts with breakdowns.', component: <StatCardsGroupWidget />,       defaultLayout: { w: 12, h: 2,  x: 0, y: Infinity }, category: 'Overview'    },
  map:        { name: 'Live Alert Map', description: 'Active sensor alerts on the map with clustering, heatmap and one-click work orders.', component: <LiveGisMapWidget />,           defaultLayout: { w: 8,  h: 6,  x: 0, y: Infinity }, category: 'Monitoring'  },
  risk:       { name: 'Assets at Risk (ML)', description: 'Assets the failure model predicts will fail, with prescriptive actions.', component: <AssetsAtRiskWidget />,         defaultLayout: { w: 4,  h: 6,  x: 8, y: Infinity }, category: 'AI/ML'       },
  
  // --- 🚀 ENTERPRISE UPDATE: Passed fetchAll={true} to work order widgets ---
  workorders: { name: 'Work Orders Chart', description: 'Created vs completed work orders per month.', component: <WorkOrdersChartWidget fetchAll={true} />,      defaultLayout: { w: 5,  h: 5,  x: 0, y: Infinity }, category: 'Analytics'   },
  // --- END UPDATE ---

  issues:     { name: 'Recent Issues', description: 'Triage desk of unresolved citizen / staff issues — convert them into work orders.', component: <RecentIssuesWidget />,         defaultLayout: { w: 4,  h: 5,  x: 5, y: Infinity }, category: 'Monitoring'  },
  lowstock:   { name: 'Low Stock Items', description: 'Parts below reorder threshold, with one-click reorder.', component: <LowStockWidget />,             defaultLayout: { w: 3,  h: 5,  x: 9, y: Infinity }, category: 'Inventory'   },
  inventory:  { name: 'Inventory Valuation', description: 'Total value of current stock and top categories.', component: <InventoryValuationWidget />,   defaultLayout: { w: 3,  h: 3,  x: 0, y: Infinity }, category: 'Inventory'   },
  
  // --- 🚀 ENTERPRISE UPDATE: Passed fetchAll={true} to work order widgets ---
  donut:      { name: 'WO Status Donut', description: 'Live split of work orders by status — click a slice for its jobs.', component: <WorkOrderStatusDonutWidget fetchAll={true} />, defaultLayout: { w: 5,  h: 5,  x: 3, y: Infinity }, category: 'Analytics'   },
  // --- END UPDATE ---

  radial:     { name: 'System Health', description: 'API, database, ML service, Ollama and Whisper health.', component: <SystemHealthRadialWidget />,   defaultLayout: { w: 4,  h: 5,  x: 8, y: Infinity }, category: 'Monitoring'  },
  // --- UPDATE: Register SLA Tracker in the widget registry ---
  sla:        { name: 'SLA Tracker', description: 'SLA compliance, at-risk / breached counts and countdowns.', component: <SlaTrackerWidget />,           defaultLayout: { w: 4,  h: 6,  x: 8, y: Infinity }, category: 'Monitoring'  },
  // --- END UPDATE ---
};

// ============================================================================
// DEFAULT LAYOUT CONFIG
// ============================================================================
const defaultLayoutsConfig = {
  layouts: {
    lg: [
      { i: 'stats', x: 0,  y: 0, w: 12, h: 2 },
      // --- UPDATE: FlowSense AI next to the SLA tracker, ecosystem at the bottom
      { i: 'ai',    x: 0,  y: 2, w: 8,  h: 8 },
      { i: 'sla',   x: 8,  y: 2, w: 4,  h: 8 },
      { i: 'map',   x: 0,  y: 10, w: 8, h: 6 },
      { i: 'risk',  x: 8,  y: 10, w: 4, h: 6 },
      { i: 'modules', x: 0, y: 16, w: 12, h: 6 },
      // --- END UPDATE
    ],
  },
  // --- UPDATE: default visible widgets now include FlowSense AI + Ecosystem ---
  visibleWidgetKeys: ['stats', 'ai', 'sla', 'map', 'risk', 'modules'],
  // --- END UPDATE ---
};

// --- UPDATE: live activity count (reads the shared FlowSense feed)
const LiveActivityBadge = () => {
  const { feed } = useFlowSenseIntel();
  if (!feed?.total) return null;
  return (
    <Tooltip label="Live events received since this page opened (work orders, sensors, AI predictions, roads, assets)" hasArrow>
      <Badge colorScheme="purple" borderRadius="full" px={2} aria-label={`${feed.total} live events`}>{feed.total} events</Badge>
    </Tooltip>
  );
};
// --- END UPDATE

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const DashboardPage = () => {
  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  const [user, setUser]                         = useState(null);
  const [layouts, setLayouts]                   = useState(defaultLayoutsConfig.layouts);
  const [visibleWidgetKeys, setVisibleWidgetKeys] = useState(defaultLayoutsConfig.visibleWidgetKeys);
  const [isLoadingLayout, setIsLoadingLayout]   = useState(true);
  const [savedLayouts, setSavedLayouts]         = useState(null);

  // FIX: Dashboard starts locked by default
  const [isLocked, setIsLocked]                 = useState(true);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSave, setAutoSave]                 = useState(false);
  const [isFullscreen, setIsFullscreen]         = useState(false);
  const [lastSaved, setLastSaved]               = useState(null);
  const [tabIndex, setTabIndex]                 = useState(0);

  // --- UPDATE: Grab Global WebSocket Status ---
  const { isConnected } = useNotifications();

  const toast    = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  // --- UPDATE: FlowSense guide drawer + AI banner
  const { isOpen: isGuideOpen, onOpen: onGuideOpen, onClose: onGuideClose } = useDisclosure();
  const [aiBannerDismissed, setAiBannerDismissed] = useState(() => {
    try { return localStorage.getItem(AI_BANNER_KEY) === '1'; } catch (_) { return false; }
  });
  const dismissAiBanner = useCallback(() => {
    setAiBannerDismissed(true);
    try { localStorage.setItem(AI_BANNER_KEY, '1'); } catch (_) { /* private mode */ }
  }, []);
  const AI_TAB_INDEX = 2;
  // --- END UPDATE

  // --------------------------------------------------------------------------
  // THEME COLORS — ALL hooks called unconditionally at the top level
  // --------------------------------------------------------------------------
  const widgetBg         = useColorModeValue('white', 'gray.700');
  const welcomeTextColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor      = useColorModeValue('gray.200', 'gray.600');
  const headerBg         = useColorModeValue('white', 'gray.800');
  const bgColor          = useColorModeValue('gray.50', 'gray.900');
  const headingColor     = useColorModeValue('gray.800', 'white');
  const mutedTextColor   = useColorModeValue('gray.500', 'gray.400');
  const accentColor      = useColorModeValue('blue.500', 'blue.400');
  const successColor     = useColorModeValue('green.500', 'green.400');
  const headerGradient   = useColorModeValue(
    'linear(to-r, blue.500, purple.600)',
    'linear(to-r, blue.400, purple.500)'
  );
  const widgetHoverBorder = useColorModeValue('blue.400', 'blue.500');
  const emptyStateBg      = useColorModeValue('gray.100', 'gray.800');
  const autoSaveBg        = useColorModeValue('green.50', 'green.900');

  // FIX: ESLint rules-of-hooks — hoisted from inline Tab _hover props
  const tabHoverBg = useColorModeValue('gray.100', 'gray.700');

  // --------------------------------------------------------------------------
  // LAYOUT PERSISTENCE
  // --------------------------------------------------------------------------
  const loadLayout = useCallback(async () => {
    setIsLoadingLayout(true);
    try {
      const savedLayoutConfig = await getDashboardLayout();
      if (savedLayoutConfig && savedLayoutConfig.layouts && savedLayoutConfig.visibleWidgetKeys) {
        setLayouts(savedLayoutConfig.layouts);
        setVisibleWidgetKeys(savedLayoutConfig.visibleWidgetKeys);
        setSavedLayouts(savedLayoutConfig.layouts);
      } else {
        setLayouts(defaultLayoutsConfig.layouts);
        setVisibleWidgetKeys(defaultLayoutsConfig.visibleWidgetKeys);
        setSavedLayouts(defaultLayoutsConfig.layouts);
      }
    } catch (error) {
      console.error('DashboardPage: An error occurred while loading layout.', error);
      toast({
        title: 'Error loading layout',
        description: 'Could not load your saved dashboard. Loading defaults.',
        status: 'error',
      });
      setLayouts(defaultLayoutsConfig.layouts);
      setVisibleWidgetKeys(defaultLayoutsConfig.visibleWidgetKeys);
    } finally {
      setIsLoadingLayout(false);
    }
  }, [toast]);

  useEffect(() => {
    setUser(getUserInfo());
    loadLayout();
  }, [loadLayout]);

  // --------------------------------------------------------------------------
  // HANDLERS — all wrapped in useCallback for perf
  // --------------------------------------------------------------------------
  const handleSaveLayout = useCallback(async (silent = false) => {
    const layoutToSave = { layouts, visibleWidgetKeys };
    try {
      await saveDashboardLayout(layoutToSave);
      setSavedLayouts(layouts);
      setHasUnsavedChanges(false);
      setLastSaved(new Date());

      if (!silent) {
        toast({
          title: 'Layout Saved',
          description: 'Your dashboard layout has been saved successfully.',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: 'Could not save your layout. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      console.error('DashboardPage: Save FAILED.', error);
    }
  }, [layouts, visibleWidgetKeys, toast]);

  // Auto-save effect
  useEffect(() => {
    if (autoSave && hasUnsavedChanges) {
      const timer = setTimeout(() => {
        handleSaveLayout(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [autoSave, hasUnsavedChanges, handleSaveLayout]);

  const onLayoutChange = useCallback((layout, allLayouts) => {
    if (isLocked) return;
    setLayouts((prev) => ({ ...prev, ...allLayouts }));
    setHasUnsavedChanges(true);
  }, [isLocked]);

  const handleUndoChanges = useCallback(() => {
    if (savedLayouts) {
      setLayouts(savedLayouts);
      setHasUnsavedChanges(false);
      toast({
        title: 'Changes Reverted',
        description: 'Dashboard restored to last saved state.',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    }
  }, [savedLayouts, toast]);

  const handleResetToDefault = useCallback(() => {
    setLayouts(defaultLayoutsConfig.layouts);
    setVisibleWidgetKeys(defaultLayoutsConfig.visibleWidgetKeys);
    setHasUnsavedChanges(true);
    toast({
      title: 'Dashboard Reset',
      description: 'Dashboard restored to default layout.',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  }, [toast]);

  const handleAddWidget = useCallback((widgetKey) => {
    const widget = ALL_AVAILABLE_WIDGETS[widgetKey];
    if (!widget) return;

    setVisibleWidgetKeys((prev) => {
      if (prev.includes(widgetKey)) {
        toast({
          title: 'Widget Already Added',
          description: 'This widget is already on your dashboard.',
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
        return prev;
      }
      setHasUnsavedChanges(true);
      return [...prev, widgetKey];
    });

    setLayouts((prevLayouts) => {
      const newLayouts = { ...prevLayouts };
      Object.keys(newLayouts).forEach((breakpoint) => {
        newLayouts[breakpoint] = [
          ...(newLayouts[breakpoint] || []),
          { ...widget.defaultLayout, i: widgetKey },
        ];
      });
      return newLayouts;
    });

    onClose();

    toast({
      title: 'Widget Added',
      description: `${widget.name} has been added to your dashboard.`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  }, [onClose, toast]);

  const handleRemoveWidget = useCallback((widgetKey) => {
    const widget = ALL_AVAILABLE_WIDGETS[widgetKey];
    setVisibleWidgetKeys((prev) => prev.filter((key) => key !== widgetKey));
    setHasUnsavedChanges(true);

    toast({
      title: 'Widget Removed',
      description: `${widget?.name || 'Widget'} has been removed from your dashboard.`,
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  }, [toast]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // --------------------------------------------------------------------------
  // WIDGET WRAPPER — useCallback so it's stable for useMemo below
  // --------------------------------------------------------------------------
  const wrapWidget = useCallback((key, content) => (
    <Box
      key={key}
      bg={widgetBg}
      p={5}
      borderRadius="2xl"
      boxShadow="md"
      overflow="hidden"
      position="relative"
      borderWidth="2px"
      borderColor={borderColor}
      className={isLocked ? '' : 'draggable-handle'}
      cursor={isLocked ? 'default' : 'grab'}
      _active={isLocked ? {} : { cursor: 'grabbing' }}
      transition="all 0.2s ease-in-out"
      _hover={
        isLocked
          ? {}
          : {
              boxShadow: 'xl',
              borderColor: widgetHoverBorder,
              transform: 'translateY(-2px)',
            }
      }
    >
      {/* Gradient indicator strip — only visible when unlocked */}
      {!isLocked && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="4px"
          bgGradient={headerGradient}
          opacity={0.8}
        />
      )}

      {/* Remove button — only visible when unlocked */}
      {!isLocked && (
        <Tooltip label="Remove widget" hasArrow>
          <IconButton
            aria-label="Remove widget"
            icon={<CloseIcon />}
            size="xs"
            position="absolute"
            top={3}
            right={3}
            zIndex={10}
            onClick={() => handleRemoveWidget(key)}
            colorScheme="red"
            variant="solid"
            borderRadius="full"
            boxShadow="md"
            transition="transform 0.2s"
            _hover={{ transform: 'scale(1.1)' }}
            _active={{ transform: 'scale(0.9)' }}
          />
        </Tooltip>
      )}

      {/* --- UPDATE: "?" explains the widget (sits left of the remove button when unlocked) */}
      <Box position="absolute" top={3} right={isLocked ? 3 : 12} zIndex={10} onMouseDown={(e) => e.stopPropagation()}>
        <WidgetHelpButton widgetKey={key} />
      </Box>
      {/* --- END UPDATE */}

      {/* UPDATE: Suspense boundary handles the loading state of the Lazy components */}
      <Suspense
        fallback={
          <Center h="100%">
            <Spinner size="lg" color="blue.500" thickness="3px" speed="0.8s" />
          </Center>
        }
      >
        {content}
      </Suspense>
    </Box>
  ), [isLocked, widgetBg, borderColor, widgetHoverBorder, headerGradient, handleRemoveWidget]);

  // --------------------------------------------------------------------------
  // MEMOIZED GRID WIDGETS — only recalculates when keys or wrapper change
  // --------------------------------------------------------------------------
  const gridWidgets = useMemo(() => {
    return visibleWidgetKeys.map((key) => {
      const widget = ALL_AVAILABLE_WIDGETS[key];
      if (!widget) return null;
      return wrapWidget(key, widget.component);
    });
  }, [visibleWidgetKeys, wrapWidget]);

  // --------------------------------------------------------------------------
  // LOADING STATE
  // --------------------------------------------------------------------------
  if (isLoadingLayout) {
    return (
      <Center h="calc(100vh - 200px)">
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          textAlign="center"
        >
          <Box position="relative" mb={6}>
            <Spinner
              size="xl"
              thickness="4px"
              speed="0.65s"
              color={accentColor}
            />
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              w="60px"
              h="60px"
              bgGradient={headerGradient}
              borderRadius="full"
              opacity={0.2}
              animation="pulse 2s infinite"
            />
          </Box>
          <Heading size="md" mb={3} color={headingColor}>
            Loading Your Dashboard
          </Heading>
          <Text fontSize="sm" color={mutedTextColor}>
            Personalizing your workspace...
          </Text>
        </MotionBox>
      </Center>
    );
  }

  // --------------------------------------------------------------------------
  // MAIN RENDER
  // --------------------------------------------------------------------------
  return (
    <FlowSenseIntelProvider>{/* --- UPDATE: one shared live snapshot for every AI widget */}
    <Box
      h={isFullscreen ? '100vh' : 'auto'}
      overflow={isFullscreen ? 'hidden' : 'visible'}
      bg={bgColor}
      minH="100vh"
    >
      <Tabs
        variant="soft-rounded"
        colorScheme="blue"
        isLazy
        index={tabIndex}
        onChange={(index) => setTabIndex(index)}
      >
        {/* ================================================================ */}
        {/* HEADER                                                           */}
        {/* ================================================================ */}
        <MotionBox
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            pt={{
              base: isFullscreen ? '20px' : '130px',
              md:   isFullscreen ? '20px' : '80px',
              xl:   isFullscreen ? '20px' : '80px',
            }}
            px={{ base: '20px', md: '30px' }}
            pb={4}
            bg={headerBg}
            borderBottomWidth="2px"
            borderBottomColor={borderColor}
            position={isFullscreen ? 'sticky' : 'relative'}
            top={0}
            zIndex={100}
            boxShadow="sm"
          >
            {/* ---------- Title Row + Action Buttons ---------- */}
            <Flex
              justify="space-between"
              align="center"
              mb={4}
              flexWrap="wrap"
              gap={4}
            >
              {/* Left: Logo + Title + Meta */}
              <Box>
                <HStack spacing={3} mb={2}>
                  {/* Animated gradient icon */}
                  <MotionBox
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ duration: 0.6, type: 'spring' }}
                  >
                    <Box
                      w="48px"
                      h="48px"
                      bgGradient={headerGradient}
                      borderRadius="xl"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      boxShadow="lg"
                    >
                      <Icon as={SettingsIcon} color="white" boxSize={6} />
                    </Box>
                  </MotionBox>

                  <Box>
                    <HStack spacing={2} align="center">
                      <Heading
                        size={{ base: 'lg', md: 'xl' }}
                        bgGradient={headerGradient}
                        bgClip="text"
                        fontWeight="800"
                      >
                        FlowSense Dashboard
                      </Heading>

                      <AnimatePresence>
                        {hasUnsavedChanges && tabIndex === 0 && (
                          <MotionBox
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                          >
                            <Badge
                              colorScheme="orange"
                              fontSize="xs"
                              borderRadius="full"
                              px={3}
                              py={1}
                            >
                              Unsaved
                            </Badge>
                          </MotionBox>
                        )}
                      </AnimatePresence>

                      {isLocked && tabIndex === 0 && (
                        <Badge
                          colorScheme="gray"
                          fontSize="xs"
                          borderRadius="full"
                          px={3}
                          py={1}
                        >
                          <HStack spacing={1}>
                            <Icon as={LockIcon} boxSize={2.5} />
                            <Text>Locked</Text>
                          </HStack>
                        </Badge>
                      )}
                    </HStack>

                    <HStack spacing={2} color={welcomeTextColor} fontSize="sm" mt={1}>
                      <Text fontWeight="500">
                        Welcome back,{' '}
                        <Text as="span" color={accentColor} fontWeight="600">
                          {user?.username || 'user'}
                        </Text>
                        !
                      </Text>
                      {lastSaved && tabIndex === 0 && (
                        <>
                          <Text>•</Text>
                          <HStack spacing={1}>
                            <Icon as={FaSave} boxSize={3} />
                            <Text fontSize="xs">{lastSaved.toLocaleTimeString()}</Text>
                          </HStack>
                        </>
                      )}
                    </HStack>
                  </Box>
                </HStack>
              </Box>

              {/* --- UPDATE: FlowSense AI + guide (all tabs) */}
              <HStack spacing={2}>
                <LiveActivityBadge />
                <Button size="md" borderRadius="xl" colorScheme="purple" variant={tabIndex === AI_TAB_INDEX ? 'solid' : 'outline'}
                  leftIcon={<Icon as={MdAutoAwesome} />} onClick={() => setTabIndex(AI_TAB_INDEX)} aria-label="Open FlowSense AI">
                  FlowSense AI
                </Button>
                <Tooltip label="FlowSense guide — modules, AI engines, widgets" hasArrow>
                  <IconButton size="md" borderRadius="xl" variant="ghost" icon={<Icon as={InfoOutlineIcon} />} onClick={onGuideOpen} aria-label="Open FlowSense guide" />
                </Tooltip>
              </HStack>
              {/* --- END UPDATE */}

              {/* Right: Action buttons — only on Overview tab */}
              {tabIndex === 0 && (
                <HStack spacing={2} flexWrap="wrap">
                  {/* --- UPDATE: Global WebSocket Status Badge --- */}
                  <Tooltip label={`WebSocket Status: ${isConnected ? 'connected' : 'disconnected'}`} hasArrow>
                      <Flex
                          align="center"
                          bg={isConnected ? 'green.50' : 'red.50'}
                          color={isConnected ? 'green.600' : 'red.600'}
                          px={3}
                          py={1.5}
                          borderRadius="xl"
                          borderWidth="1px"
                          borderColor={isConnected ? 'green.200' : 'red.200'}
                      >
                          <Icon as={isConnected ? MdWifi : MdWifiOff} mr={2} />
                          <Text fontSize="xs" fontWeight="700" textTransform="uppercase">
                              {isConnected ? 'Live Data' : 'Offline'}
                          </Text>
                          {isConnected && (
                              <MotionBox
                                  ml={2} w="8px" h="8px" bg="green.500" borderRadius="full"
                                  animate={pulseAnimation.animate}
                                  transition={pulseAnimation.transition}
                              />
                          )}
                      </Flex>
                  </Tooltip>
                  {/* --- END UPDATE --- */}

                  {/* Auto-save Toggle */}
                  <Tooltip label="Enable auto-save" hasArrow>
                    <Box
                      bg={autoSave ? autoSaveBg : 'transparent'}
                      px={3}
                      py={2}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor={autoSave ? successColor : borderColor}
                      transition="all 0.2s"
                    >
                      <FormControl display="flex" alignItems="center" w="auto">
                        <FormLabel
                          htmlFor="auto-save"
                          mb={0}
                          fontSize="xs"
                          mr={2}
                          fontWeight="600"
                        >
                          Auto-save
                        </FormLabel>
                        <Switch
                          id="auto-save"
                          size="sm"
                          isChecked={autoSave}
                          onChange={(e) => setAutoSave(e.target.checked)}
                          colorScheme="green"
                        />
                      </FormControl>
                    </Box>
                  </Tooltip>

                  {/* Undo */}
                  <Tooltip label="Undo changes" hasArrow>
                    <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <IconButton
                        icon={<Icon as={FaUndo} />}
                        size="md"
                        onClick={handleUndoChanges}
                        isDisabled={!hasUnsavedChanges}
                        variant="outline"
                        aria-label="Undo changes"
                        borderRadius="xl"
                      />
                    </MotionBox>
                  </Tooltip>

                  {/* Lock / Unlock */}
                  <Tooltip label={isLocked ? 'Unlock layout' : 'Lock layout'} hasArrow>
                    <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <IconButton
                        icon={<Icon as={isLocked ? UnlockIcon : LockIcon} />}
                        size="md"
                        onClick={() => setIsLocked(!isLocked)}
                        colorScheme={isLocked ? 'gray' : 'blue'}
                        variant={isLocked ? 'solid' : 'outline'}
                        aria-label="Toggle lock"
                        borderRadius="xl"
                      />
                    </MotionBox>
                  </Tooltip>

                  {/* Fullscreen */}
                  <Tooltip
                    label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                    hasArrow
                  >
                    <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <IconButton
                        icon={<Icon as={isFullscreen ? FaCompress : FaExpand} />}
                        size="md"
                        onClick={toggleFullscreen}
                        variant="outline"
                        aria-label="Toggle fullscreen"
                        borderRadius="xl"
                      />
                    </MotionBox>
                  </Tooltip>

                  {/* Settings Menu */}
                  <Menu>
                    <Tooltip label="Dashboard settings" hasArrow>
                      <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <MenuButton
                          as={IconButton}
                          icon={<SettingsIcon />}
                          size="md"
                          variant="outline"
                          aria-label="Settings"
                          borderRadius="xl"
                        />
                      </MotionBox>
                    </Tooltip>
                    <MenuList borderRadius="xl" boxShadow="xl">
                      <MenuItem
                        icon={<RepeatIcon />}
                        onClick={handleResetToDefault}
                        borderRadius="lg"
                        fontSize="sm"
                      >
                        Reset to Default
                      </MenuItem>
                      <MenuItem
                        icon={<Icon as={FaSave} />}
                        onClick={() => handleSaveLayout()}
                        borderRadius="lg"
                        fontSize="sm"
                      >
                        Save Layout
                      </MenuItem>
                    </MenuList>
                  </Menu>

                  {/* Save Button */}
                  <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      colorScheme="blue"
                      size="md"
                      leftIcon={<Icon as={FaSave} />}
                      onClick={() => handleSaveLayout()}
                      isDisabled={!hasUnsavedChanges}
                      borderRadius="xl"
                      fontWeight="600"
                      boxShadow="sm"
                      _hover={{ boxShadow: 'md' }}
                    >
                      Save
                    </Button>
                  </MotionBox>

                  {/* Add Widget Button */}
                  <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      colorScheme="green"
                      size="md"
                      leftIcon={<AddIcon />}
                      onClick={onOpen}
                      borderRadius="xl"
                      fontWeight="600"
                      boxShadow="sm"
                      _hover={{ boxShadow: 'md' }}
                    >
                      Add Widget
                    </Button>
                  </MotionBox>
                </HStack>
              )}
            </Flex>

            {/* ---------- Tab Navigation ---------- */}
            <TabList borderBottom="none" mt={2}>
              <Tab
                borderRadius="lg"
                fontWeight="600"
                mr={2}
                _selected={{ bg: 'blue.500', color: 'white' }}
                _hover={{ bg: tabHoverBg }}
              >
                Overview Dashboard
              </Tab>
              <Tab
                borderRadius="lg"
                fontWeight="600"
                _selected={{ bg: 'purple.500', color: 'white' }}
                _hover={{ bg: tabHoverBg }}
              >
                Command Center (Live Grid)
              </Tab>
              {/* --- UPDATE */}
              <Tab
                borderRadius="lg"
                fontWeight="600"
                ml={2}
                _selected={{ bg: 'pink.500', color: 'white' }}
                _hover={{ bg: tabHoverBg }}
              >
                FlowSense AI & Guide
              </Tab>
              {/* --- END UPDATE */}
            </TabList>

            {/* ---------- Unsaved Changes Warning ---------- */}
            <AnimatePresence>
              {hasUnsavedChanges && !autoSave && tabIndex === 0 && (
                <MotionBox
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  mt={4}
                >
                  <Alert
                    status="warning"
                    borderRadius="xl"
                    size="sm"
                    boxShadow="sm"
                    variant="left-accent"
                  >
                    <AlertIcon />
                    <Text fontSize="sm" fontWeight="500">
                      You have unsaved changes. Click "Save" to keep your layout.
                    </Text>
                  </Alert>
                </MotionBox>
              )}
            </AnimatePresence>
          </Box>
        </MotionBox>

        {/* ================================================================ */}
        {/* TAB PANELS                                                       */}
        {/* ================================================================ */}
        <TabPanels p={0}>
          {/* ---- Tab 1: Overview Dashboard (Drag-and-Drop Grid) ---- */}
          <TabPanel p={0}>
            <Box p={{ base: '20px', md: '30px' }}>
              {/* --- UPDATE: point existing layouts at the new AI */}
              {!visibleWidgetKeys.includes('ai') && !aiBannerDismissed && (
                <Alert status="info" variant="left-accent" borderRadius="xl" mb={4} colorScheme="purple">
                  <AlertIcon as={MdAutoAwesome} />
                  <Text fontSize="sm" flex={1}>FlowSense AI can brief you on the whole system and answer questions about every module.</Text>
                  <HStack spacing={2} ml={2}>
                    <Button size="xs" colorScheme="purple" onClick={() => handleAddWidget('ai')}>Add to dashboard</Button>
                    <Button size="xs" variant="ghost" onClick={() => setTabIndex(AI_TAB_INDEX)}>Open</Button>
                    <IconButton size="xs" variant="ghost" icon={<CloseIcon boxSize={2} />} onClick={dismissAiBanner} aria-label="Dismiss" />
                  </HStack>
                </Alert>
              )}
              {/* --- END UPDATE */}
              <ResponsiveGridLayout
                className="layout"
                layouts={layouts}
                onLayoutChange={onLayoutChange}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={60}
                draggableHandle=".draggable-handle"
                isDraggable={!isLocked}
                isResizable={!isLocked}
                compactType="vertical"
                preventCollision={false}
              >
                {gridWidgets}
              </ResponsiveGridLayout>

              {/* Empty state */}
              {visibleWidgetKeys.length === 0 && (
                <MotionBox
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Center h="500px">
                    <Box
                      textAlign="center"
                      bg={emptyStateBg}
                      p={12}
                      borderRadius="3xl"
                      borderWidth="2px"
                      borderStyle="dashed"
                      borderColor={borderColor}
                      maxW="500px"
                    >
                      <MotionBox
                        initial={{ y: 0 }}
                        animate={{ y: [-5, 5, -5] }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        <Box
                          w="80px"
                          h="80px"
                          mx="auto"
                          mb={6}
                          bgGradient={headerGradient}
                          borderRadius="2xl"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          boxShadow="xl"
                        >
                          <Icon as={AddIcon} boxSize={10} color="white" />
                        </Box>
                      </MotionBox>

                      <Heading size="lg" mb={3} color={headingColor}>
                        Your Dashboard is Empty
                      </Heading>
                      <Text fontSize="md" color={mutedTextColor} mb={6}>
                        Click "Add Widget" to start building your personalized dashboard
                      </Text>

                      <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                          colorScheme="green"
                          size="lg"
                          leftIcon={<AddIcon />}
                          onClick={onOpen}
                          borderRadius="xl"
                          fontWeight="600"
                          boxShadow="lg"
                          _hover={{ boxShadow: 'xl' }}
                        >
                          Add Your First Widget
                        </Button>
                      </MotionBox>
                    </Box>
                  </Center>
                </MotionBox>
              )}
            </Box>
          </TabPanel>

          {/* ---- Tab 2: Command Center (Live Grid) ---- */}
          <TabPanel p={0}>
            <Box p={{ base: '20px', md: '30px' }}>
              <Suspense
                fallback={
                  <Center h="100vh">
                    <Spinner size="xl" color="blue.500" />
                  </Center>
                }
              >
                <LiveGrid />
              </Suspense>
            </Box>
          </TabPanel>

          {/* ---- Tab 3: FlowSense AI & Guide ---- */}
          {/* --- UPDATE */}
          <TabPanel p={0}>
            <Box p={{ base: '20px', md: '30px' }}>
              <Suspense fallback={<Center h="300px"><Spinner size="xl" color="purple.500" /></Center>}>
                <Box bg={widgetBg} p={5} borderRadius="2xl" boxShadow="md" borderWidth="2px" borderColor={borderColor} mb={6} minH="520px" display="flex" flexDirection="column">
                  <FlowSenseAiCommandCenter variant="full" />
                </Box>
                <Box bg={widgetBg} p={5} borderRadius="2xl" boxShadow="md" borderWidth="2px" borderColor={borderColor}>
                  <FlowSenseModulesWidget />
                </Box>
              </Suspense>
            </Box>
          </TabPanel>
          {/* --- END UPDATE */}
        </TabPanels>
      </Tabs>

      {/* ================================================================ */}
      {/* WIDGET LIBRARY DRAWER                                            */}
      {/* ================================================================ */}
      <Suspense fallback={null}>
        <WidgetLibraryDrawer
          isOpen={isOpen}
          onClose={onClose}
          allWidgets={ALL_AVAILABLE_WIDGETS}
          currentWidgetKeys={visibleWidgetKeys}
          onAddWidget={handleAddWidget}
        />
      </Suspense>

      {/* --- UPDATE: FlowSense guide */}
      <DashboardAiHelper
        isOpen={isGuideOpen}
        onClose={onGuideClose}
        visibleWidgetKeys={visibleWidgetKeys}
        onAddWidget={handleAddWidget}
        onOpenAi={() => setTabIndex(AI_TAB_INDEX)}
      />
      {/* --- END UPDATE */}
    </Box>
    </FlowSenseIntelProvider>
  );
};

export default DashboardPage;